import type { ProjectionInputs, ProjectionLine, ProjectionStep } from './reapproProjection.types.js'
import { applyProjectionCaps, type CapCandidate } from './reapproProjectionCaps.tools.js'
import {
  fixedLine,
  isRelevant,
  type PendingLine,
  projectedLine,
  type SeriesState,
} from './reapproProjectionLine.tools.js'
import { addDaysIso, addLot, expireLots } from './reapproProjectionLots.tools.js'
import { runSharedStep, sellGroupUntil, sellingGroups } from './reapproProjectionShared.tools.js'

export {
  computedLine,
  type ComputedLineFields,
  fixedUnitsKey,
} from './reapproProjectionLine.tools.js'

/** Horizon de la projection du réappro, en jours : la plus longue plage que lit son endpoint. */
export const PROJECTION_HORIZON_DAYS = 31

const schedule = (pending: PendingLine) => {
  if (!pending.step.drivesStock || pending.line.units === null) return
  const { incoming } = pending.state
  const delivery = pending.line.deliveryDateIso
  incoming.set(delivery, (incoming.get(delivery) ?? 0) + pending.line.units)
}

const applyCaps = (inputs: ProjectionInputs, pendings: PendingLine[]) => {
  const candidates = new Map<CapCandidate, PendingLine>()
  for (const pending of pendings) {
    if (!pending.step.capped || pending.line.source !== 'projected' || pending.line.units === null)
      continue
    const product = inputs.products.get(pending.line.reference)
    if (!product) continue
    candidates.set(
      {
        entityKey: pending.line.entityKey,
        reference: pending.line.reference,
        deliveryIso: pending.line.deliveryDateIso,
        besoin: pending.line.units,
        colisage: Math.max(1, product.colisage),
        maxProduction: product.maxProduction,
        capGroup: product.capGroup,
      },
      pending,
    )
  }
  for (const [candidate, outcome] of applyProjectionCaps([...candidates.keys()])) {
    const pending = candidates.get(candidate)
    if (!pending) continue
    pending.line.units = outcome.units
    pending.line.productCapReached = outcome.productCapReached
    pending.line.groupCapReached = outcome.groupCapReached
  }
}

const runSteps = (
  inputs: ProjectionInputs,
  states: SeriesState[],
  steps: ProjectionStep[],
  dayIso: string | null,
): PendingLine[] => {
  const pendings: PendingLine[] = []
  for (const step of steps) {
    if (step.sharedDemand) {
      pendings.push(...runSharedStep(inputs, states, step, dayIso))
      continue
    }
    for (const state of states) {
      const product = inputs.products.get(state.input.reference)
      if (!product || !isRelevant(inputs, state, step, product)) continue
      const fixed = fixedLine(inputs, state, step, product)
      if (fixed) pendings.push({ line: fixed, state, step })
      else if (!step.isPast && dayIso !== null) {
        pendings.push({ line: projectedLine(inputs, state, step, product, dayIso), state, step })
      }
    }
  }
  applyCaps(inputs, pendings)
  for (const pending of pendings) schedule(pending)
  return pendings
}

const arrivingUnits = (inputs: ProjectionInputs, state: SeriesState, dayIso: string) => {
  const scheduled = state.incoming.get(dayIso) ?? 0
  if (dayIso !== inputs.runDateIso) return scheduled
  if (state.input.runDateArrivalDisabled) return 0
  return Math.max(0, scheduled - state.input.receivedOnRunDate)
}

const arrive = (inputs: ProjectionInputs, state: SeriesState, dayIso: string) => {
  const product = inputs.products.get(state.input.reference)
  if (!product || state.unavailable) return
  state.lots = addLot(state.lots, {
    expiresOnIso: addDaysIso(dayIso, product.dlcDays - 1),
    units: arrivingUnits(inputs, state, dayIso),
  })
}

const fillBreakage = (pendings: PendingLine[], runDateIso: string) => {
  const previousByState = new Map<SeriesState, string>()
  const ordered = pendings
    .filter((pending) => pending.step.drivesStock && pending.line.units !== null)
    .sort((a, b) => a.line.deliveryDateIso.localeCompare(b.line.deliveryDateIso))
  for (const pending of ordered) {
    const from = previousByState.get(pending.state) ?? runDateIso
    let breakage = 0
    for (let day = from; day < pending.line.deliveryDateIso; day = addDaysIso(day, 1)) {
      breakage += pending.state.breakageByDay.get(day) ?? 0
    }
    pending.line.projectedBreakage = breakage
    previousByState.set(pending.state, pending.line.deliveryDateIso)
  }
}

export const simulateReapproProjection = (inputs: ProjectionInputs): ProjectionLine[] => {
  const states: SeriesState[] = inputs.series.map((input) => ({
    input,
    lots: [...input.initialLots].sort((a, b) => a.expiresOnIso.localeCompare(b.expiresOnIso)),
    incoming: new Map(),
    breakageByDay: new Map(),
    soldShare: 0,
    unavailable: false,
  }))
  const pendings = runSteps(
    inputs,
    states,
    inputs.steps.filter((step) => step.isPast),
    null,
  )
  const futureSteps = inputs.steps.filter((step) => !step.isPast)
  const groups = sellingGroups(inputs, states)

  for (let day = inputs.runDateIso; day <= inputs.horizonEndIso; day = addDaysIso(day, 1)) {
    for (const state of states) {
      arrive(inputs, state, day)
      state.soldShare =
        day === inputs.runDateIso
          ? inputs.salesShareBeforeHour(state.input.entityKey, inputs.runHour)
          : 0
    }
    const hours = [
      ...new Set(
        futureSteps.filter((step) => step.computeDateIso === day).map((step) => step.computeHour),
      ),
    ].sort((a, b) => a - b)
    for (const hour of hours) {
      for (const group of groups) {
        sellGroupUntil(group, day, (state) =>
          inputs.salesShareBeforeHour(state.input.entityKey, hour),
        )
      }
      const steps = futureSteps.filter(
        (step) => step.computeDateIso === day && step.computeHour === hour,
      )
      pendings.push(...runSteps(inputs, states, steps, day))
    }
    for (const group of groups) sellGroupUntil(group, day, () => 1)
    for (const state of states) {
      const { lots, expired } = expireLots(state.lots, day)
      state.lots = lots
      state.breakageByDay.set(day, expired)
    }
  }

  fillBreakage(pendings, inputs.runDateIso)
  return pendings.map((pending) => pending.line)
}
